import * as React from 'react';
import './pageSection.module.scss';
import { TitleBox } from './TitleBox';
import { TrialLpContext, TrialSectionContext } from '../../contexts/trial';
import { useContext, useEffect, useState } from 'react';
import { LoaderRow } from '../../components/atoms/layout/LoaderRow';
import { useObserver } from 'mobx-react';
import { animated, useSpring } from 'react-spring';
import { TrackerOption, useVisibilityTracker } from '../../../commonv3/hooks/useVisibilityTracker';
import { EventCategory, SheEventClient } from '../../../commonv3/events/SheEventClient';

const createTrackerOptions = (id): TrackerOption => {
    const intervalSec = 1;
    return {
        onUpdateVisibleDuration: (cumulativeDurationSec) => {
            SheEventClient.reportEvent(EventCategory.TRIAL, `view_section_${id}`, {
                _ga_value: intervalSec,
                duration: intervalSec,
                cumulative_duration: cumulativeDurationSec,
            });
        },
        reportIntervalSec: intervalSec,
        visibilityThreshold: 0.2,
    };
};

type Props = {
    id?: string;
    className?: string;
    styleName?: string;
    title: string | JSX.Element;
    subTitle: string;
    disabled?: boolean;
    titleColor?: 'white' | 'black';
    children: any;
    titleFont?: 'europa' | 'yu-gothic';
};

export const PageSection = ({
    id,
    className,
    title,
    subTitle,
    titleColor = 'black',
    disabled = false,
    children,
    titleFont = 'europa',
}: Props) => {
    const pageContext = useContext(TrialLpContext);
    return useObserver(() => {
        const { resource } = pageContext;
        const loadCompleted = resource.isDependentSectionRendered(id) && resource.isCompleted(id);
        const styleProps = useSpring({
            opacity: loadCompleted ? 1 : 0,
            top: loadCompleted ? '0' : '40px',
            position: 'relative',
        });
        useEffect(
            () => {
                if (loadCompleted) {
                    resource.reportRender(id);
                }
            },
            [loadCompleted]
        );
        const [ref, setRef] = useState(null);
        useVisibilityTracker(ref, createTrackerOptions(id));
        if (!loadCompleted) {
            return <LoaderRow />;
        }
        return (
            <animated.div id={id} style={styleProps} styleName="page-section" className={className} ref={setRef}>
                <div styleName="title-wrapper">
                    <TitleBox
                        title={title}
                        subTitle={subTitle}
                        disabled={disabled}
                        titleColor={titleColor}
                        fontFamily={titleFont}
                    />
                </div>
                <div styleName="page-body-wrapper">
                    <div styleName="page-body">
                        <TrialSectionContext.Provider value={{ id: id, loaded: loadCompleted }}>
                            {children}
                        </TrialSectionContext.Provider>
                    </div>
                </div>
            </animated.div>
        );
    });
};

export const TitlelessPageSection = ({
    id,
    className = undefined,
    styleName = undefined,
    setRef = undefined,
    children,
}) => {
    const pageContext = useContext(TrialLpContext);
    return useObserver(() => {
        const { resource } = pageContext;
        const loadCompleted = resource.isCompleted(id) && resource.isDependentSectionCompleted(id);
        const styleProps = useSpring({ opacity: loadCompleted ? 1 : 0, top: loadCompleted ? '20px' : '0' });
        useEffect(
            () => {
                if (loadCompleted) {
                    resource.reportRender(id);
                }
            },
            [loadCompleted]
        );
        const [ref, _setRef] = useState(null);
        useVisibilityTracker(ref, createTrackerOptions(id));
        const handleRef = (r) => {
            _setRef(r);
            if (setRef) {
                setRef(r);
            }
        };
        if (!loadCompleted) {
            return <LoaderRow />;
        }
        return (
            <animated.div id={id} style={styleProps} className={className} ref={handleRef}>
                <TrialSectionContext.Provider value={{ id: id, loaded: loadCompleted }}>
                    {children}
                </TrialSectionContext.Provider>
            </animated.div>
        );
    });
};
